import { useQuery } from "@apollo/client/react";
import {
  PowersDataDocument,
  type PowersDataQuery,
} from "@/graphql/generated/operations";

export type PowerLevelEntry = NonNullable<PowersDataQuery["selfAssessmentResults"]>[number];

export interface UsePowersDataResult {
  results: readonly PowerLevelEntry[];
  // Self-assessment levels (back-compat alias for selfByElement).
  levelByElement: Map<string, number | null | undefined>;
  selfByElement: Map<string, number | null | undefined>;
  smartByElement: Map<string, number | null | undefined>;
  loading: boolean;
  error: Error | null;
}

// Powers data for the PowersSidebar (Save · Profile). Every Person has all
// 30 powers implicitly via the elements taxonomy; this hook surfaces the
// optional measured-level overlay from both evaluations (self-assessment +
// smart analysis) so the sidebar can switch between them.
export function usePowersData(options?: { skip?: boolean }): UsePowersDataResult {
  const { data, loading, error } = useQuery(PowersDataDocument, { skip: options?.skip });
  const results = data?.selfAssessmentResults ?? [];
  const selfByElement = new Map<string, number | null | undefined>(
    results.map((r) => [String(r.element), r.result]),
  );
  const smartByElement = new Map<string, number | null | undefined>(
    (data?.smartAnalysisResults ?? []).map((r) => [String(r.element), r.score]),
  );
  return {
    results,
    levelByElement: selfByElement,
    selfByElement,
    smartByElement,
    loading,
    error: (error as Error | undefined) ?? null,
  };
}
